import { type CSSProperties, type NestedCSSProperties } from 'typestyle/lib/types';
import StyleConstants from '../../core/styles.constants';
import DashboardTheme from '../statistics/dashboard/dashboard-theme';
import { tint } from '../../core/admin-surface.styles';

// Parkings tab chrome, following the approved parking mockup: one row per area, its two threshold
// fields, and a band that shows where the quiet/busy bands land in real spaces.

export const rows: CSSProperties = {
  padding: '0 20px',
};

export const row: NestedCSSProperties = {
  padding: '24px 0',
  borderBottom: `1px solid ${DashboardTheme.gray200}`,
  $nest: {
    '&:last-child': {
      borderBottom: 0,
    },
  },
};

export const rowTop: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  marginBottom: 16,
};

export const tile: CSSProperties = {
  width: 38,
  height: 38,
  borderRadius: 10,
  flex: '0 0 auto',
  display: 'grid',
  placeItems: 'center',
  background: tint(StyleConstants.DARK_BLUE_COLOR, 0.1),
  color: StyleConstants.DARK_BLUE_COLOR,
};

export const rowWho: CSSProperties = {
  flex: 1,
  minWidth: 0,
};

export const rowName: CSSProperties = {
  display: 'block',
  fontSize: 13.5,
  fontWeight: 600,
  color: StyleConstants.DARK_BLUE_COLOR,
  wordBreak: 'break-word',
};

export const rowCapacity: CSSProperties = {
  display: 'block',
  fontSize: 13,
  color: DashboardTheme.gray500,
};

export const warningIcon: CSSProperties = {
  flex: '0 0 auto',
  color: StyleConstants.PINK_COLOR,
  lineHeight: 0,
};

export const fields = (isMobile: boolean): CSSProperties => ({
  display: 'grid',
  gap: 20,
  alignItems: 'start',
  gridTemplateColumns: isMobile ? '1fr' : '140px 140px minmax(0, 1fr)',
});

export const field: CSSProperties = {
  position: 'relative',
};

export const fieldLabel: CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  color: DashboardTheme.gray500,
  marginBottom: 5,
  fontFamily: DashboardTheme.bodyFontFamily,
};

export const fieldInput = (hasError: boolean): NestedCSSProperties => ({
  width: '100%',
  boxSizing: 'border-box',
  padding: '9px 34px 9px 12px',
  border: `1px solid ${hasError ? StyleConstants.PINK_COLOR : DashboardTheme.gray300}`,
  borderRadius: 10,
  fontFamily: DashboardTheme.bodyFontFamily,
  fontSize: 13.5,
  color: DashboardTheme.navy800,
  background: StyleConstants.WHITE_COLOR,
  $nest: {
    '&:focus': {
      outline: `2px solid ${hasError ? StyleConstants.PINK_COLOR : StyleConstants.MEDIUM_BLUE_COLOR}`,
      outlineOffset: -1,
      borderColor: hasError ? StyleConstants.PINK_COLOR : StyleConstants.MEDIUM_BLUE_COLOR,
    },
  },
});

export const fieldUnit: CSSProperties = {
  position: 'absolute',
  right: 12,
  bottom: 10,
  fontSize: 12.5,
  color: DashboardTheme.gray500,
  pointerEvents: 'none',
};

// The band reads left to right: quiet up to the low threshold, normal in between, busy from the
// high threshold on.
export const band = (isValid: boolean): CSSProperties => ({
  position: 'relative',
  height: 8,
  borderRadius: 4,
  background: DashboardTheme.gray100,
  overflow: 'hidden',
  opacity: isValid ? 1 : 0.35,
});

export const bandNormal = (leftPercentage: number, widthPercentage: number): CSSProperties => ({
  position: 'absolute',
  top: 0,
  bottom: 0,
  left: `${leftPercentage}%`,
  width: `${widthPercentage}%`,
  background: StyleConstants.MEDIUM_BLUE_COLOR,
});

export const bandBusy = (leftPercentage: number): CSSProperties => ({
  position: 'absolute',
  top: 0,
  bottom: 0,
  left: `${leftPercentage}%`,
  right: 0,
  background: tint(StyleConstants.ORANGE_COLOR, 0.65),
});

export const legend = (hasError: boolean): CSSProperties => ({
  display: 'block',
  marginTop: 8,
  fontSize: 12,
  lineHeight: 1.45,
  fontFamily: DashboardTheme.bodyFontFamily,
  color: hasError ? StyleConstants.PINK_COLOR : DashboardTheme.gray500,
  fontWeight: hasError ? 600 : 400,
});

export const saveRow: CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: 16,
};

export const note: CSSProperties = {
  display: 'flex',
  gap: 12,
  alignItems: 'center',
  margin: 20,
  background: '#EAF3FC',
  color: '#1B4E7F',
  borderRadius: 10,
  padding: '12px 16px',
  fontSize: 13,
  lineHeight: 1.5,
  fontFamily: DashboardTheme.bodyFontFamily,
};

export const noteIcon: CSSProperties = {
  flex: '0 0 auto',
  color: StyleConstants.MEDIUM_BLUE_COLOR,
  lineHeight: 0,
};
